import {useEffect, useRef, useState} from 'react';
import {FieldProps, wdiCore, WdiModalAppProps} from '@/components/WdiControls';
import {ProFormInstance} from '@ant-design/pro-form';
import {EC} from '@/services/mdm/ec';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {Form, message, Tooltip} from 'antd';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {responseIsSuccess, ResponseStatus} from '@/utils/request';
import {SoldTo} from '@/services/mdm/soldto';
import {Enum} from '@/enums/system';
import {BO} from '@/services/mdm/bo';
import {FormSa} from '@/services/sales/sa';
import {BudgetProject} from '@/services/sales/bp/budgetProject';
import {RevenueType} from '@/services/mdm/revenuetype';

const FormSaDetailPrevApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [boOpts, setBoOpts] = useState<any[]>();
  const [revenueTypeOpts, setRevenueTypeOpts] = useState<any[]>();
  const [budgetProject, setBudgetProject] = useState<API.BudgetProject>();
  const [limitedEcIds, setLimitedEcIds] = useState('');
  const [expandKeysEc, setExpandKeysEc] = useState<string[]>();

  const {row, rowIndex, formSa} = props.data;

  useEffect(() => {
    if (formSa.budgetProjectId) {
      BudgetProject.get(formSa.budgetProjectId).then((response) => {
        setBudgetProject(response.data);
        setLimitedEcIds(response.data.limitedEcIds);
      });
    }
  }, [formSa.budgetProjectId]);

  const handleSoldToChange = (soldToCode: any) => {
    formRef.current?.setFieldsValue({boId: null});
    if (soldToCode) {
      BO.search({
        soldToCode: soldToCode,
        budgetProjectId: formSa?.budgetProjectId,
        boLevels: `${Enum.BOLevel.BO4}`,
        withPermissions: true,
        isActive: true,
      }).then((response) => {
        if (responseIsSuccess(response)) {
          setBoOpts(response.data.map((item) => ({value: item.boId, label: item.boName})));
          if (response.data.length === 1) {
            formRef.current?.setFieldsValue({boId: response.data[0].boId});
          }
        }
      });
    } else {
      setBoOpts([]);
    }
  };

  const handleEcChange = async (ecId: any) => {
    if (ecId) {
      const revenueTypeRes = await RevenueType.search({
        ecId: ecId,
        applyType: Enum.RevApplyType.SA_GENERAL,
      });
      if (responseIsSuccess(revenueTypeRes)) {
        setRevenueTypeOpts(revenueTypeRes.data.map((item) => ({
            value: item.revenueTypeId,
            label: item.revenueTypeName
          })),
        );
        if (revenueTypeRes.data.length === 1) {
          formRef.current?.setFieldsValue({revenueType: revenueTypeRes.data[0].revenueTypeId});
        }
      }
    }
  };

  useEffect(() => {
    if (row) {
      if (row.revenueType) {
        formRef.current?.setFieldsValue({revenueType: row.revenueType});
      }
      if (row.ecId) {
        formRef.current?.setFieldsValue({ecId: row.ecId});
        setExpandKeysEc([row.ecId]);
        handleEcChange(row.ecId).then();
      }

      if (row.soldToCode) {
        formRef.current?.setFieldsValue({
          soldToCode: {
            value: row.soldToCode,
            label: wdiCore.String.Join(row.soldToCode, row.soldToName),
          },
        });
        handleSoldToChange(row.soldToCode);
      }
      if (row.boId) {
        formRef.current?.setFieldsValue({boId: row.boId});
      }
    }
  }, [row]);

  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => {
          props.onClose?.();
        },
      }}
      submitter={{searchConfig: {submitText: '下一步'}}}
      onFinish={async (values) => {
        const {ecId, revenueType, soldToCode, boId, ...entity} = values;

        entity.ecId = WdiSelectFormat.getvalue(ecId);
        entity.revenueType = WdiSelectFormat.getvalue(revenueType);
        entity.soldToCode = WdiSelectFormat.getvalue(soldToCode);
        entity.boId = WdiSelectFormat.getvalue(boId);

        const response = await FormSa.Buttons.validatePicker({
          rowIndex: rowIndex,
          formSaDetail: entity,
          formSa: {...formSa},
        });
        if (!responseIsSuccess(response)) {
          message.error(response.message);
          return;
        }
        if (props.onSave) {
          const formSaDetail = Object.assign({}, row, {
            ecId: response.data.ecId,
            ecName: response.data.ecName,
            soldToCode: response.data.soldToCode,
            soldToName: response.data.soldToName,
            boId: response.data.boId,
            boCode: response.data.boCode,
            boName: response.data.boName,
            extendFieldsSa: response.data.extendFieldsSa,
            revExtendFieldsSa: response.data.revExtendFieldsSa,
          });

          formSaDetail.revenueType = response.data.revenueType;
          formSaDetail.revenueTypeName = response.data.revenueTypeName;
          formSaDetail.accrualMethodId = response.data.accrualMethodId;
          formSaDetail.accrualMethodName = response.data.accrualMethodName;
          props.onSave({
            code: ResponseStatus.SUCCESS,
            data: Object.assign({}, {formSaDetail: formSaDetail}, props.data),
          });
        }
      }}
    >
      <Form.Item
        name="ecId"
        label="费用项"
        rules={[{required: true, message: '费用项不能为空!'}]}
      >
        <WdiTreeSelect
          {...{
            defaultExpandKeys: expandKeysEc,
            placeholder: '请选择费用项',
            dataLoading: async (params) => {
              const response = await EC.searchEc3(Object.assign({
                toParentLevel: Enum.ECLevel.EC2,
                budgetProjectId: formSa.budgetProjectId,
                ecIds: limitedEcIds,
                pageTypeIds: String(formSa.pageTypeId),
                isActive: true,
              }, params));
              const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
                textField: 'ecName',
                valueField: 'ecId',
                id: 'ecId',
                pId: 'parentId',
                level: 'ecLevel',
                disabledLevel: [Enum.ECLevel.EC2],
              }, response.data, (node: any, item: any) => {
                node.title = (
                  <Tooltip placement="topLeft" title={item.remark}><span>{node.title}</span></Tooltip>
                );
                return node;
              });
              return {treeData, dict};
            },
            onChange: async (value) => {
              await handleEcChange(WdiSelectFormat.getvalue(value));
            },
            reloadKey: limitedEcIds,
          }}
        />
      </Form.Item>
      <Form.Item
        name="revenueType"
        label="核算类型"
        rules={[{required: true, message: '核算类型不能为空!'}]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择核算类型',
            showSearch: true,
            options: revenueTypeOpts,
          }}
        />
      </Form.Item>
      <Form.Item
        name="soldToCode"
        label="客户"
        rules={[{required: true, message: '客户不能为空!'}]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择客户',
            showSearch: true,
            remote: true,
            onChange: handleSoldToChange,
            dataLoading: async (params) => {
              const response = await SoldTo.ajaxSearch(Object.assign({
                withPermissions: true,
                isActive: true,
                budgetProjectId: formSa.budgetProjectId,
                soldToCodes: budgetProject?.limitedSoldToCodes,
                custSysCodes: budgetProject?.limitedCustSysCodes,
              }, params));
              return response.data.map((item) => ({value: item.soldToCode, label: item.codeOrName}));
            },
            reloadKey: budgetProject?.limitedSoldToCodes,
          }}
        />
      </Form.Item>
      <Form.Item
        name="boId"
        label="四级使用部门"
        rules={[{required: true, message: '四级使用部门不能为空!'}]}
      >
        <WdiSelect
          {...{
            label: "四级使用部门",
            showSearch: true,
            options: boOpts,
          }}
        />
      </Form.Item>
    </WdiModalForm>
  );
};

export default FormSaDetailPrevApp;
